import { useEffect, useRef, useState } from "react";
import { Upload, Download, Move, Trash2, RotateCcw, Package } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";

// Real-world cart dimensions used to translate on-canvas pixel sizes to inches.
const CART_DIM = { height_in: 33, width_in: 15.16, depth_in: 15.1 };

// The base cart image is 910×1117. We render it at a fixed CSS width and derive
// inches-per-pixel from the visible long side. The "side panel" long dimension
// (what we see stretched across the front of the body) maps to the cart's 33"
// height when the cart lies with feet down — for a typical rolling cooler the
// horizontal box length IS the longest dimension, so we treat 33in ≈ visible
// front-panel width for the scale ruler.
const BASE_IMG_W = 910;
const BASE_IMG_H = 1117;
// Approximate pixel bounds (in the source image) for the two brandable surfaces.
const ZONES = {
  side: { x: 95, y: 200, w: 730, h: 360, real_w_in: 33 },
  lid: { x: 470, y: 180, w: 350, h: 80, real_w_in: 15.16 },
};
const COLOR_SWATCHES = [
  { name: "Mint (Original)", hex: "#A9DDCE", rotate: 0 },
  { name: "Coral", hex: "#FF7E67", rotate: 200 },
  { name: "Sunny Yellow", hex: "#FFD57E", rotate: 220 },
  { name: "Sky Blue", hex: "#7EC8E3", rotate: 60 },
  { name: "Berry Pink", hex: "#E67EA6", rotate: 240 },
  { name: "Forest Green", hex: "#4C9F70", rotate: 340 },
  { name: "Charcoal", hex: "#4A4A4A", rotate: 0, sat: 0, brightness: 0.55 },
  { name: "Cream", hex: "#F5EAD8", rotate: 200, sat: 0.35, brightness: 1.15 },
];

function cartFilter(swatch) {
  const sat = swatch.sat === undefined ? 1 : swatch.sat;
  const brightness = swatch.brightness === undefined ? 1 : swatch.brightness;
  return `hue-rotate(${swatch.rotate}deg) saturate(${sat}) brightness(${brightness})`;
}

/**
 * Cart Branding Preview — vendors upload a logo/design and see it overlaid on
 * a cooler cart mockup. Two placement modes (side / lid / both), drag to move,
 * resize with the corner handle, opacity, cart color picker, PNG export.
 */
export default function CartBrandingPreview() {
  const [logoUrl, setLogoUrl] = useState(null);
  const [placement, setPlacement] = useState("side"); // side | lid | both
  const [opacity, setOpacity] = useState(1);
  const [swatch, setSwatch] = useState(COLOR_SWATCHES[0]);
  const [exporting, setExporting] = useState(false);
  // Each overlay stores its rect in pixel coords of the rendered stage.
  const [side, setSide] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [lid, setLid] = useState({ x: 0, y: 0, w: 0, h: 0 });

  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const fileRef = useRef(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });

  // Measure the rendered stage after image load so we can convert source-image
  // zone coords (in the 910×1117 space) to on-screen pixels.
  const measureStage = () => {
    if (!imgRef.current) return;
    const { width, height } = imgRef.current.getBoundingClientRect();
    setStageSize({ w: width, h: height });
    // Convert source-image zones to on-screen coords, and set sensible defaults
    // for each overlay (only if it's not been positioned yet).
    const sx = width / BASE_IMG_W;
    const sy = height / BASE_IMG_H;
    setSide((s) => (s.w ? s : {
      x: (ZONES.side.x + ZONES.side.w * 0.5 - 130) * sx,
      y: (ZONES.side.y + ZONES.side.h * 0.5 - 65) * sy,
      w: 260 * sx,
      h: 130 * sy,
    }));
    setLid((l) => (l.w ? l : {
      x: (ZONES.lid.x + ZONES.lid.w * 0.5 - 80) * sx,
      y: (ZONES.lid.y + ZONES.lid.h * 0.5 - 20) * sy,
      w: 160 * sx,
      h: 40 * sy,
    }));
  };

  useEffect(() => {
    window.addEventListener("resize", measureStage);
    return () => window.removeEventListener("resize", measureStage);
  }, []);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (PNG, JPG, SVG)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogoUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const inchesFor = (px, zoneKey) => {
    const zone = ZONES[zoneKey];
    const pxPerIn = (stageSize.w * (zone.w / BASE_IMG_W)) / zone.real_w_in;
    if (!pxPerIn) return "";
    return (px / pxPerIn).toFixed(1);
  };

  // ---------- Drag & resize ----------
  const pointerRef = useRef(null);
  const startDrag = (key, mode, e) => {
    if (!logoUrl) return;
    e.preventDefault();
    const rect = key === "side" ? side : lid;
    pointerRef.current = {
      key,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origX: rect.x,
      origY: rect.y,
      origW: rect.w,
      origH: rect.h,
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };
  const onPointerMove = (e) => {
    const p = pointerRef.current;
    if (!p) return;
    const dx = e.clientX - p.startX;
    const dy = e.clientY - p.startY;
    const set = p.key === "side" ? setSide : setLid;
    const rect = p.key === "side" ? side : lid;
    if (p.mode === "move") {
      set({ ...rect, x: p.origX + dx, y: p.origY + dy });
    } else {
      // Preserve aspect ratio if the source image has loaded.
      const aspect = p.origH / p.origW || 0.5;
      const newW = Math.max(30, p.origW + dx);
      const newH = newW * aspect;
      set({ ...rect, w: newW, h: newH });
    }
  };
  const onPointerUp = () => {
    pointerRef.current = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  };

  const resetPositions = () => {
    setSide((s) => ({ ...s, w: 0 }));
    setLid((l) => ({ ...l, w: 0 }));
    setTimeout(measureStage, 20);
  };

  const removeLogo = () => {
    setLogoUrl(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const download = async () => {
    if (!stageRef.current) return;
    setExporting(true);
    try {
      const { default: html2canvas } = await import("html2canvas-pro");
      const canvas = await html2canvas(stageRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `cart-branding-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("PNG downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't export PNG");
    } finally {
      setExporting(false);
    }
  };

  const showSide = placement === "side" || placement === "both";
  const showLid = placement === "lid" || placement === "both";

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="cart-branding-page">
      <div className="mb-6">
        <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163] mb-2">
          <Package className="w-3.5 h-3.5" style={{ color: "#8EAC50" }} /> Baker's Tools
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl text-[#3B2C24]">Cart Branding Preview</h1>
        <p className="text-sm text-[#5A4A40] mt-1">
          Upload your logo or design and preview it on your cooler cart before you order the print.
          Cart dimensions: <b>{CART_DIM.height_in}"H × {CART_DIM.width_in}"W × {CART_DIM.depth_in}"D</b>.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Controls */}
        <aside className="lg:col-span-4 space-y-4">
          <div className="cp-card p-4 space-y-3" data-testid="upload-panel">
            <label className="text-xs font-semibold text-[#3B2C24] uppercase tracking-wide block">1. Your logo or design</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onFileChange}
              className="hidden"
              data-testid="logo-file-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full py-3 rounded-2xl border-2 border-dashed border-[#EBDCD1] hover:border-[#FF7E67]/40 text-sm font-semibold text-[#3B2C24] flex items-center justify-center gap-2"
              data-testid="upload-logo-btn"
            >
              <Upload className="w-4 h-4 text-[#FF7E67]" /> {logoUrl ? "Replace image" : "Upload PNG / JPG / SVG"}
            </button>
            {logoUrl && (
              <button
                onClick={removeLogo}
                className="w-full text-xs text-[#857163] hover:text-[#B23B3B] flex items-center justify-center gap-1"
                data-testid="remove-logo-btn"
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            )}
          </div>

          <div className="cp-card p-4 space-y-3">
            <label className="text-xs font-semibold text-[#3B2C24] uppercase tracking-wide block">2. Placement</label>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { k: "side", label: "Side panel" },
                { k: "lid", label: "Lid" },
                { k: "both", label: "Both" },
              ].map((opt) => (
                <button
                  key={opt.k}
                  onClick={() => setPlacement(opt.k)}
                  className={`px-2 py-2 rounded-xl text-xs font-semibold transition ${placement === opt.k ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"}`}
                  data-testid={`placement-${opt.k}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="cp-card p-4 space-y-3">
            <label className="text-xs font-semibold text-[#3B2C24] uppercase tracking-wide block">3. Opacity</label>
            <Slider
              value={[Math.round(opacity * 100)]}
              onValueChange={(v) => setOpacity((v[0] || 0) / 100)}
              min={20}
              max={100}
              step={5}
              data-testid="opacity-slider"
            />
            <div className="text-xs text-[#857163] text-right">{Math.round(opacity * 100)}%</div>
          </div>

          <div className="cp-card p-4 space-y-3" data-testid="color-panel">
            <label className="text-xs font-semibold text-[#3B2C24] uppercase tracking-wide block">4. Cart color</label>
            <div className="grid grid-cols-4 gap-1.5">
              {COLOR_SWATCHES.map((c) => (
                <button
                  key={c.name}
                  onClick={() => setSwatch(c)}
                  title={c.name}
                  className={`h-9 rounded-xl border-2 transition ${swatch.name === c.name ? "border-[#3B2C24] ring-2 ring-[#FF7E67]/40" : "border-[#EBDCD1] hover:border-[#3B2C24]/40"}`}
                  style={{ background: c.hex }}
                  data-testid={`color-${c.name.split(" ")[0].toLowerCase()}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input
                type="color"
                value={swatch.hex}
                onChange={(e) => {
                  // Custom color: pick a hue-rotate from the RGB. Rough conversion:
                  // convert hex → HSL hue and rotate from mint's ~150°.
                  const hex = e.target.value;
                  const r = parseInt(hex.slice(1, 3), 16) / 255;
                  const g = parseInt(hex.slice(3, 5), 16) / 255;
                  const b = parseInt(hex.slice(5, 7), 16) / 255;
                  const max = Math.max(r, g, b), min = Math.min(r, g, b);
                  let h = 0;
                  const d = max - min;
                  if (d !== 0) {
                    if (max === r) h = ((g - b) / d) % 6;
                    else if (max === g) h = (b - r) / d + 2;
                    else h = (r - g) / d + 4;
                  }
                  h = Math.round(h * 60);
                  if (h < 0) h += 360;
                  const rotate = (h - 150 + 360) % 360;
                  const sat = d < 0.05 ? 0.25 : 1;
                  setSwatch({ name: "Custom", hex, rotate, sat });
                }}
                className="w-10 h-10 rounded-xl border border-[#EBDCD1] cursor-pointer"
                data-testid="custom-color-picker"
              />
              <span className="text-xs text-[#857163]">Custom color</span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={resetPositions}
              className="flex-1 py-2.5 rounded-2xl border border-[#EBDCD1] text-sm font-semibold text-[#3B2C24] hover:bg-[#F9EFE6] flex items-center justify-center gap-1"
              data-testid="reset-positions-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset positions
            </button>
            <button
              onClick={download}
              disabled={exporting}
              className="flex-1 py-2.5 rounded-2xl bg-[#FF7E67] text-white font-semibold text-sm hover:opacity-90 transition disabled:opacity-60 flex items-center justify-center gap-1"
              data-testid="download-png-btn"
            >
              <Download className="w-4 h-4" /> {exporting ? "Rendering…" : "Download PNG"}
            </button>
          </div>
        </aside>

        {/* Stage */}
        <div className="lg:col-span-8">
          <div className="cp-card p-4">
            <div
              ref={stageRef}
              className="relative mx-auto"
              style={{ maxWidth: "700px", background: "#ffffff", borderRadius: 16 }}
              data-testid="cart-stage"
            >
              <img
                ref={imgRef}
                src="/cart-mockup-base.jpg"
                alt="Cooler cart"
                className="w-full h-auto block"
                style={{ filter: cartFilter(swatch) }}
                onLoad={measureStage}
                crossOrigin="anonymous"
              />
              {logoUrl && showSide && (
                <OverlayLayer
                  rect={side}
                  onDragStart={(mode, e) => startDrag("side", mode, e)}
                  logoUrl={logoUrl}
                  opacity={opacity}
                  label={`Side · ${inchesFor(side.w, "side")}" wide`}
                  testid="overlay-side"
                />
              )}
              {logoUrl && showLid && (
                <OverlayLayer
                  rect={lid}
                  onDragStart={(mode, e) => startDrag("lid", mode, e)}
                  logoUrl={logoUrl}
                  opacity={opacity}
                  label={`Lid · ${inchesFor(lid.w, "lid")}" wide`}
                  testid="overlay-lid"
                />
              )}
              {!logoUrl && (
                <div className="absolute inset-x-0 bottom-4 text-center text-xs text-[#857163] px-4 pointer-events-none">
                  Upload a logo to preview it on your cart
                </div>
              )}
            </div>
            <div className="mt-3 text-[11px] text-[#857163] flex items-center gap-1 justify-center">
              <Move className="w-3 h-3" /> Drag the logo to reposition · Drag the corner handle to resize
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OverlayLayer({ rect, onDragStart, logoUrl, opacity, label, testid }) {
  return (
    <div
      className="absolute select-none"
      style={{
        left: rect.x,
        top: rect.y,
        width: rect.w,
        height: rect.h,
        opacity,
        cursor: "move",
        touchAction: "none",
      }}
      onPointerDown={(e) => onDragStart("move", e)}
      data-testid={testid}
    >
      <img
        src={logoUrl}
        alt="Overlay logo"
        draggable={false}
        className="w-full h-full pointer-events-none"
        style={{ objectFit: "contain" }}
      />
      {/* Resize handle */}
      <div
        onPointerDown={(e) => { e.stopPropagation(); onDragStart("resize", e); }}
        className="absolute -bottom-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#FF7E67] border-2 border-white shadow"
        style={{ cursor: "nwse-resize" }}
        data-testid={`${testid}-resize`}
      />
      {/* Selection outline + label */}
      <div className="absolute inset-0 border-2 border-dashed border-[#FF7E67]/70 pointer-events-none rounded-sm" />
      <div className="absolute -top-6 left-0 text-[10px] uppercase tracking-widest font-semibold text-[#FF7E67] bg-white/90 px-1.5 py-0.5 rounded pointer-events-none">
        {label}
      </div>
    </div>
  );
}
